import { useState, useEffect, useRef } from "react";
import { X, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDebounce } from "@/hooks/use-debounce";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Game {
  universeId: number;
  name: string;
  thumbnail: string;
  creator: string;
  playerCount?: number;
}

interface GameSelectionProps {
  selectedGames: Game[];
  onChange: (games: Game[]) => void;
}

export function GameSelection({ selectedGames, onChange }: GameSelectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 500);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isClickingItem, setIsClickingItem] = useState(false);

  useEffect(() => {
    if (debouncedSearch.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    const fetchGames = async () => {
      setIsLoading(true);
      try {
        const searchResponse = await fetch(
          `https://apis.roproxy.com/search-api/omni-search?searchQuery=${encodeURIComponent(
            debouncedSearch,
          )}&sessionId=pageType=games`,
        );

        if (!searchResponse.ok)
          throw new Error("Failed to fetch games search results");

        const searchData = await searchResponse.json();
        const allGames: any[] = [];

        searchData.searchResults?.forEach((group: any) => {
          if (
            group.contentGroupType === "Game" &&
            Array.isArray(group.contents)
          ) {
            allGames.push(...group.contents);
          }
        });

        if (allGames.length === 0) {
          setSearchResults([]);
          setIsLoading(false);
          return;
        }

        const universeIds = allGames
          .map((game: any) => game.universeId)
          .filter(Boolean);

        if (universeIds.length === 0) {
          setSearchResults([]);
          setIsLoading(false);
          return;
        }

        const gameDetailsResponse = await fetch(
          `https://games.roproxy.com/v1/games?universeIds=${universeIds.join(",")}`,
        );

        if (!gameDetailsResponse.ok)
          throw new Error("Failed to fetch game details");

        const gameDetailsData = await gameDetailsResponse.json();
        const gameDetails = gameDetailsData.data || [];
        const thumbnailsResponse = await fetch(
          `https://thumbnails.roproxy.com/v1/games/multiget/thumbnails?universeIds=${universeIds.join(",")}&format=png&size=768x432&isCircular=false`,
        );

        if (!thumbnailsResponse.ok)
          throw new Error("Failed to fetch game thumbnails");

        const thumbnailsData = await thumbnailsResponse.json();
        const thumbnailsMap: Record<string, string> = {};

        thumbnailsData.data.forEach((item: any) => {
          const universeId = item.universeId.toString();
          const thumbnailUrl = item.thumbnails?.[0]?.imageUrl || "";
          if (thumbnailUrl) {
            thumbnailsMap[universeId] = thumbnailUrl;
          }
        });

        const games: Game[] = gameDetails.map((game: any) => {
          const universeId = game.id;
          return {
            universeId,
            name: game.name,
            thumbnail:
              thumbnailsMap[universeId.toString()] ||
              "https://t0.rbxcdn.com/placeholder",
            creator: game.creator?.name || "Unknown Creator",
            playerCount: game.playing || 0,
          };
        });

        setSearchResults(games);
      } catch (error) {
        console.error("Error fetching games:", error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGames();
  }, [debouncedSearch]);

  const handleAddGame = (game: Game) => {
    setIsOpen(false);
    if (!selectedGames.some((g) => g.universeId === game.universeId)) {
      onChange([...selectedGames, game]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemoveGame = (universeId: number) => {
    onChange(selectedGames.filter((game) => game.universeId !== universeId));
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="flex items-center space-x-2">
          <div className="relative w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for Roblox games..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim().length > 0) {
                  setIsOpen(true);
                } else {
                  setIsOpen(false);
                }
              }}
              className="pl-9"
              onFocus={() => {
                if (searchQuery.trim().length > 0) {
                  setIsOpen(true);
                }
              }}
              onBlur={(e) => {
                if (!isClickingItem) {
                  setIsOpen(false);
                }
              }}
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>

        {isOpen && searchResults.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-10 mt-1 w-full rounded-md border border-input bg-background shadow-md"
            onMouseDown={() => setIsClickingItem(true)}
            onMouseUp={() => setIsClickingItem(false)}
          >
            <ScrollArea className="h-60">
              <div className="p-1">
                {searchResults.map((game) => (
                  <div
                    key={game.universeId}
                    className="flex cursor-pointer items-center space-x-2 rounded-md p-2 hover:bg-accent"
                    onClick={() => handleAddGame(game)}
                  >
                    <div className="h-12 w-20 overflow-hidden rounded-md">
                      <img
                        src={game.thumbnail}
                        alt={game.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate text-sm font-medium">
                        {game.name}
                      </p>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <span>{game.creator}</span>
                        {game.playerCount !== undefined && (
                          <span className="ml-1">
                            • {game.playerCount.toLocaleString()} playing
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {isOpen &&
          debouncedSearch.trim().length >= 3 &&
          searchResults.length === 0 &&
          !isLoading && (
            <div className="absolute z-10 mt-1 w-full rounded-md border border-input bg-background p-4 text-center shadow-md">
              <p className="text-sm text-muted-foreground">No games found</p>
            </div>
          )}
      </div>

      {selectedGames.length > 0 && (
        <div className="mt-2">
          <p className="mb-1 text-sm text-muted-foreground">Selected games:</p>
          <div className="flex flex-wrap gap-2">
            {selectedGames.map((game) => (
              <Badge
                key={game.universeId}
                variant="secondary"
                className="flex items-center gap-1 px-3 py-1.5"
              >
                <span className="max-w-[150px] truncate">{game.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0"
                  onClick={() => handleRemoveGame(game.universeId)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove {game.name}</span>
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
